import type { Card } from '../games/heads-up-poker';
import type { Role, ServerMsg } from '../proto';
import { isMpsContext, type MpsReveal } from './mps-context';
import type { PartialShare } from './poker-crypto';

export interface MpsBoardHandState {
  context: string;
  hostPublicKey: Uint8Array | null;
  guestPublicKey: Uint8Array | null;
  shuffleRound1Deck: string[] | null;
  shuffleRound1Proof: string | null;
  finalDeck: string[] | null;
  finalDeckProof: string | null;
  /** Only opposite-role shares received from the relay belong here. */
  partialByIndex: Map<number, { host?: PartialShare; guest?: PartialShare }>;
  /** Recipient-local results: never copied into a public message or history. */
  bridgeHands: [Card[], Card[]] | null;
  bridgeReveal: MpsReveal | null;
  myHole: [Card, Card] | null;
  holeReveal: MpsReveal | null;
  boardRevealByIndex: Map<number, PartialShare>;
  revealedBoard: Card[];
  cryptoReady: boolean;
  cheatDetected: boolean;
  cryptoError: boolean;
}
export type MpsLocalResults = Partial<Pick<MpsBoardHandState,
  'bridgeHands' | 'bridgeReveal' | 'myHole' | 'holeReveal' | 'boardRevealByIndex' | 'revealedBoard' | 'cryptoReady' | 'cheatDetected' | 'cryptoError'
>>;

export function emptyMpsBoardHand(context: string): MpsBoardHandState {
  return { context, hostPublicKey: null, guestPublicKey: null, shuffleRound1Deck: null, shuffleRound1Proof: null, finalDeck: null, finalDeckProof: null, partialByIndex: new Map(), bridgeHands: null, bridgeReveal: null, myHole: null, holeReveal: null, boardRevealByIndex: new Map(), revealedBoard: [], cryptoReady: false, cheatDetected: false, cryptoError: false };
}

export function applyMpsLocalResults(current: MpsBoardHandState | undefined, context: string, update: MpsLocalResults): MpsBoardHandState | undefined {
  if (!current || current.context !== context || !isMpsContext(context)) return current;
  if ((update.holeReveal && update.holeReveal.context !== context) || (update.bridgeReveal && update.bridgeReveal.context !== context)) return current;
  if (Object.entries(update).every(([key, value]) => current[key as keyof MpsLocalResults] === value)) return current;
  return { ...current, ...update };
}

type MpsServerMessage = Extract<ServerMsg, { t: 'mps_context' | 'mps_keys_ready' | 'mps_shuffle_ready' | 'mps_partial_decrypt_ready' }>;

/** An announced context is the only operation allowed to create/reset a deal entry. */
export function applyMpsServerMessage(current: MpsBoardHandState | undefined, msg: MpsServerMessage, recipient: Role | null): MpsBoardHandState | undefined {
  if (!isMpsContext(msg.context)) return current;
  if (msg.t === 'mps_context') return current?.context === msg.context ? current : emptyMpsBoardHand(msg.context);
  if (!current || current.context !== msg.context) return current;
  if (msg.t === 'mps_keys_ready') {
    const validKey = (key: number[]) => key.length === 32 && key.every(byte => Number.isInteger(byte) && byte >= 0 && byte <= 255);
    if (!validKey(msg.host_public_key) || !validKey(msg.guest_public_key)) return current;
    return { ...current, hostPublicKey: Uint8Array.from(msg.host_public_key), guestPublicKey: Uint8Array.from(msg.guest_public_key) };
  }
  if (msg.t === 'mps_shuffle_ready') {
    if ((msg.round !== 1 && msg.round !== 2) || msg.deck.length !== 52 || msg.deck.some(card => !/^[0-9a-fA-F]{128}$/.test(card)) || typeof msg.proof !== 'string') return current;
    return msg.round === 1 ? { ...current, shuffleRound1Deck: [...msg.deck], shuffleRound1Proof: msg.proof } : { ...current, finalDeck: [...msg.deck], finalDeckProof: msg.proof };
  }
  if (!recipient || recipient === 'spectator' || msg.from === recipient || msg.from === 'spectator') return current;
  if (msg.card_indices.length !== msg.shares.length || msg.card_indices.length !== msg.proofs.length || new Set(msg.card_indices).size !== msg.card_indices.length) return current;
  if (msg.card_indices.some(index => !Number.isInteger(index) || index < 0 || index >= 52) || msg.shares.some(share => !/^[0-9a-fA-F]{64}$/.test(share)) || msg.proofs.some(proof => !/^[0-9a-fA-F]{128}$/.test(proof))) return current;
  const partialByIndex = new Map(current.partialByIndex);
  msg.card_indices.forEach((index, i) => { partialByIndex.set(index, { ...partialByIndex.get(index), [msg.from]: { share: msg.shares[i], proof: msg.proofs[i] } }); });
  return { ...current, partialByIndex };
}

/** A newly announced hand invalidates in-flight work for the prior hand on that board. */
export function applyMpsMessageToMap(current: ReadonlyMap<string, MpsBoardHandState>, msg: MpsServerMessage, recipient: Role | null): ReadonlyMap<string, MpsBoardHandState> {
  const key = `${msg.board}:${msg.hand}`;
  const old = current.get(key);
  const entry = applyMpsServerMessage(old, msg, recipient);
  if (!entry || entry === old) return current;
  const next = new Map(current);
  if (msg.t === 'mps_context') for (const existing of next.keys()) {
    if (existing.startsWith(`${msg.board}:`) && existing !== key) next.delete(existing);
  }
  next.set(key, entry);
  return next;
}
